/* eslint-disable max-lines */
import React, { lazy, Component } from 'react';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';

import ControlledFeatureService from 'services/ControlledFeatureService';
import { translate } from 'services/i18nService';
import createMessagingService from 'services/MessagingService/MessagingService';
import { URL_PATHNAME_UPDATED } from 'services/MessagingService/EventNames';
import UserStore from 'stores/UserStore';
import { EDIT_TWO_FA_SETUP, RESEND_TWO_FA_SETUP } from 'pages/ConfirmPasswordPage/Api';
import UserProfile from 'pages/UserProfile';
import FanClubs from 'pages/FanClubs';
import TwitterCallbackPage from 'components/BroadcasterTwitter/TwitterCallbackPage';
import TwitterConnectPage from 'components/BroadcasterTwitter/TwitterConnectPage';
import {
  DIRECTORY_CAMS_ALIASES,
  GENDER_PREFERENCE_REGEX,
  POPULAR_CAMS_REGEX,
} from 'enum/helpers';
import { ENUM_TWO_FA } from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { updateHreflangLinks } from 'utils/helpers';

import DefaultLayout from './layouts/DefaultLayout';
import HeadlessLayout from './layouts/HeadlessLayout';


const Achievements = lazy(() => import('../pages/Achievements/Achievements'));
const AddExistingPerformer = lazy(() =>
  import('pages/AddExistingPerformer')
);
const AdminAuthPage = lazy(() =>
  import('pages/AdminAuth/AdminAuthPage')
);
const AdminBannedUsers = lazy(() =>
  import('pages/AdminBannedUsers/AdminBannedUsers')
);
const AdminBoostReports = lazy(() =>
  import('pages/AdminBoostReports/AdminBoostReports')
);
const AdminBulkBan = lazy(() =>
  import('pages/AdminBulkBan/AdminBulkBan')
);
const AdminCsvMessagesPage = lazy(() =>
  import('pages/AdminCsvMessages/AdminCsvMessagesPage')
);
const AdminDashboard = lazy(() =>
  import('pages/AdminDashboard/AdminDashboard')
);
const AdminGifts = lazy(() =>
  import('pages/AdminGifts/AdminGifts')
);
const AdminLanguageOverridePage = lazy(() =>
  import('pages/AdminLanguageOverride/AdminLanguageOverridePage')
);
const AdminLogsPage = lazy(() =>
  import('pages/AdminLogs/AdminLogsPage')
);
const AdminPrivateShowRatings = lazy(() =>
  import('pages/AdminPrivateShowRatings/AdminPrivateShowRatings')
);
const AdminSeo = lazy(() =>
  import('pages/AdminSEO/AdminSEO')
);
const AdminSponsoredTags = lazy(() =>
  import('pages/AdminSponsoredTags/AdminSponsoredTags')
);
const AppBroadcasting = lazy(() =>
  import('pages/AppBroadcasting')
);
const Awards = lazy(() =>
  import('pages/AwardsAdmin/AwardsAdmin')
);
const BitsafeClosePage = lazy(() =>
  import('pages/PerformerPayment/Bitsafe/BitsafeClosePage')
);
const Broadcaster = lazy(() =>
  import('pages/Broadcaster/Broadcaster')
);
const BroadcastingInfo = lazy(() =>
  import('pages/BroadcastingInfo/BroadcastingInfo')
);
const Calendar = lazy(() =>
  import('pages/Calendar/Calendar')
);
const CheckoutPage = lazy(() =>
  import('pages/CheckoutPage/CheckoutPage')
);
const ComingSoon = lazy(() =>
  import('pages/ComingSoon/ComingSoon')
);
const ConfirmPasswordPage = lazy(() =>
  import('pages/ConfirmPasswordPage/ConfirmPasswordPage')
);
const BeyondTheDeck = lazy(() =>
  import('pages/promos/BeyondTheDeck'));
const Contact = lazy(() =>
  import('pages/Contact/Contact')
);
const Contest = lazy(() =>
  import('pages/Contest/Contest')
);
const Countries = lazy(() =>
  import('pages/Countries/Countries')
);
const CreateUser = lazy(() =>
  import('pages/AdminDashboard/CreateUser/CreateUser')
);
const DailyRevenuePage = lazy(() =>
  import('pages/StudioInsights/DailyRevenuePage/DailyRevenuePage')
);
const DashboardPage = lazy(() =>
  import('pages/DashboardPage/DashboardPage')
);
const DetailedRevenuePage = lazy(() =>
  import('pages/StudioInsights/DetailedRevenuePage/DetailedRevenuePage')
);
const Directory = lazy(() =>
  import('pages/Directory/Directory')
);
const DirectoryBannerAdmin = lazy(() =>
  import('pages/DirectoryBannerAdmin/DirectoryBannerAdmin')
);
const EmailVerification = lazy(() =>
  import('pages/User/EmailVerification/EmailVerification')
);
const ErrorHandler = lazy(() =>
  import('components/ErrorHandler/ErrorHandler')
);
const ExperimentalTools = lazy(() =>
  import('pages/AdminExperimentalTools/ExperimentalTools')
);
const Favorites = lazy(() =>
  import('pages/Favorites')
);
const ImageApprovalTool = lazy(() =>
  import('pages/ImageApprovalTool')
);
const Inbox = lazy(() =>
  import('pages/Settings/Inbox/Inbox')
);
const InboxNew = lazy(() =>
  import('pages/Settings/Inbox-new')
);
const JSB = lazy(() =>
  import('pages/JSB/JSB')
);
const Languages = lazy(() =>
  import('pages/Languages/Languages')
);
const LastCashOut = lazy(() =>
  import('pages/LastCashOut/LastCashOut')
);
const LegalContainer = lazy(() =>
  import('pages/LegalContainer/LegalContainer')
);
const MassGifts = lazy(() =>
  import('pages/MassGifts/MassGifts')
);
const MyLibraryV2 = lazy(() =>
  import('pages/MyLibraryV2/MyLibraryV2')
);
const NNLogin = lazy(() =>
  import('pages/NNLogin')
);
const OnBoardPrivacy = lazy(() =>
  import('pages/OnBoardPrivacy/OnBoardPrivacy')
);
const OnBoardingNewUser = lazy(() =>
  import('pages/OnBoardingNewUser/OnBoardingNewUser')
);
const Onboarding = lazy(() =>
  import('pages/Onboarding/Onboarding')
);
const OwnerVerification = lazy(() =>
  import('pages/OwnerVerification/OwnerVerification')
);
const PasswordReset = lazy(() =>
  import('pages/PasswordReset/PasswordReset')
);
const PasswordResetThankYou = lazy(() =>
  import('pages/PasswordResetThankYou/PasswordResetThankYou')
);
const PerformerEditProfile = lazy(() =>
  import('pages/PerformerEditProfile/PerformerEditProfile')
);
const PerformerProfile = lazy(() =>
  import('pages/PerformerProfile/PerformerProfile')
);
const PreLander = lazy(() =>
  import('pages/PreLander/PreLander')
);
const PrivateDirectory = lazy(() =>
  import('pages/PrivateDirectory/PrivateDirectory')
);
const ProfileConfirmation = lazy(() =>
  import('components/ProfileConfirmation/ProfileConfirmation')
);
const RedirectingNotFound = lazy(() =>
  import('components/RedirectingNotFound/RedirectingNotFound')
);
const RosterManagement = lazy(() =>
  import('pages/StudioRoster/RosterManagement/RosterManagement')
);
const Settings = lazy(() =>
  import('pages/Settings/Settings')
);
const Spam = lazy(() =>
  import('pages/Spam/Spam')
);
const SpamDetector = lazy(() =>
  import('pages/AdminDashboard/SpamDetector/SpamDetector')
);
const StudioInvitation = lazy(() =>
  import('pages/StudioInvitation/StudioInvitation')
);
const StudioLogin = lazy(() =>
  import('pages/Studio/StudioLogin')
);
const StudioManagement = lazy(() =>
  import('pages/AdminDashboard/StudioManagement/StudioManagement')
);
const StudioOnboardPerformerIdentity = lazy(() =>
  import('pages/OnBoardID/StudioOnboardPerformerIdentity')
);
const StudioOnboardingRejectMessage = lazy(() =>
  import('components/StudioOnboardingRejectMessage/StudioOnboardingRejectMessage')
);
const StudioOwnerVerification = lazy(() =>
  import('pages/StudioOwnerVerification/StudioOwnerVerification')
);
const StudioPerformerIdentities = lazy(() =>
  import('pages/StudioPerformerIdentities/StudioPerformerIdentitiesPage')
);
const StudioPerformerOnBoardingConfirmation = lazy(() =>
  import('pages/StudioPerformerOnBoardingConfirmation/StudioPerformerOnBoardingConfirmation')
);
const StudioRegistration = lazy(() =>
  import('pages/Studio/StudioRegistration')
);
const StudioSettings = lazy(() =>
  import('pages/StudioSettings/StudioSettings')
);
const StudioVerificationEmail = lazy(() =>
  import('pages/StudioVerificationEmail/StudioVerificationEmail')
);
const Tags = lazy(() =>
  import('pages/Tags/Tags')
);
const TokenInfoContainer = lazy(() =>
  import('pages/TokenInfoContainer/TokenInfoContainer')
);
const TopEarningPerformers = lazy(() =>
  import('pages/StudioEarningReports/TopEarningPerformers')
);
const UpgradeToGoldPage = lazy(() =>
  import('pages/UpgradeToGoldPage')
);
const UserLogin = lazy(() =>
  import('pages/User/UserLogin')
);
const VerifyAlternateEmail = lazy(() =>
  import('pages/VerifyAlternateEmail/VerifyAlternateEmail')
);
const VerifyMyAge = lazy(() =>
  import('pages/VerifyMyAge/VerifyMyAge')
);
const WeakPasswordPage = lazy(() =>
  import('pages/WeakPasswordPage/WeakPasswordPage')
);
const WelcomePageViewer = lazy(() =>
  import('pages/WelcomePageViewer/WelcomePageViewer')
);

export default class Main extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    paymentActions: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
  };

  constructor(...args) {
    super(...args);

    this.messagingService = createMessagingService();
  }

  componentDidMount() {
    this.updateLinks();
    this.shouldBuyTokensModalRender();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.location.pathname !== this.props.location.pathname ||
      prevProps.location.search !== this.props.location.search
    ) {
      this.updateLinks();
      this.messagingService.emit(URL_PATHNAME_UPDATED, {
        prevLocation: prevProps.location,
        currentLocation: this.props.location,
      });
    }
  }

  shouldBuyTokensModalRender = () => {
    const pageQueryParams = new URLSearchParams(location.search).get('showBuyTokensModal');
    if (pageQueryParams) {
      this.props.paymentActions.showBuyTokensModal();
    }
  };

  updateCanonicalLink = () => {
    const canonicalNode = document.querySelector('link[rel="canonical"]');

    if (!canonicalNode) {
      return;
    }

    const hostURL = new URL(window.location.href);
    hostURL.search = '';

    canonicalNode.setAttribute('href', hostURL.toString().toLowerCase());
  };

  updateLinks = () => {
    this.updateCanonicalLink();
    updateHreflangLinks();
  };

  checkFeature = (feature) => {
    const { user } = this.props;

    return ControlledFeatureService.setUser(user).has(feature);
  };

  get privateDirectory() {
    return this.checkFeature(allUserGroups.PREMIUM_PRIVATE_SHOW);
  }

  get awardsV2() {
    return this.checkFeature(allUserGroups.AWARDS_V2);
  }

  get adminLogV2() {
    return this.checkFeature(allUserGroups.ADMIN_LOG_V2);
  }

  get adminBannedUsernamesV2() {
    return this.checkFeature(allUserGroups.ADMIN_BANNED_USERNAMES_V2);
  }

  get giftsRevamp() {
    return this.checkFeature(allUserGroups.GIFTS_REVAMP);
  }

  render() {
    const { props } = this;
    const forgotPasswordTwoFaContext = {
      translate,
      validateTwoFaUrl: EDIT_TWO_FA_SETUP.replace('{username}', UserStore.user.username),
      resendTwoFaUrl: RESEND_TWO_FA_SETUP.replace('{username}', UserStore.user.username),
      urlEncoded: true,
      withCredentials: true,
      formDataForSubmit: ENUM_TWO_FA.FORGOT_PASSWORD_SUBMIT,
      formDataForResend: ENUM_TWO_FA.FORGOT_PASSWORD_RESEND,
    };

    return (
      <Switch>
        <DefaultLayout Page={PreLander} exact={true} path={routeDefinitions.preLander} {...props} />
        <DefaultLayout
          Page={BeyondTheDeck}
          exact={true}
          {...props}
          path={routeDefinitions.beyondthedeck}
        />
        <DefaultLayout
          Page={ConfirmPasswordPage}
          exact={true}
          path={routeDefinitions.confirmPassword}
          twoFaContext={forgotPasswordTwoFaContext}
          {...props}
        />
        <DefaultLayout Page={PasswordReset} exact={true} path={routeDefinitions.passwordReset} {...props} />
        <DefaultLayout
          Page={PasswordResetThankYou}
          exact={true}
          path={routeDefinitions.passwordResetThankYou}
          {...props}
        />
        <DefaultLayout Page={Tags} exact={true} path="/tags" {...props} />
        <DefaultLayout Page={Tags} exact={true} path={routeDefinitions.tagsMale} {...props} />
        <DefaultLayout Page={Tags} exact={true} path={routeDefinitions.tagsFemale} {...props} />
        <DefaultLayout Page={Tags} exact={true} path={routeDefinitions.tagsTrans} {...props} />
        <DefaultLayout Page={Tags} exact={true} path={routeDefinitions.tagsCouples} {...props} />
        <DefaultLayout Page={Tags} exact={true} path={routeDefinitions.tagsAll} {...props} />
        <DefaultLayout Page={Tags} exact={true} path={routeDefinitions.tagsGeneral} {...props} />
        <DefaultLayout Page={ComingSoon} exact={true} path={routeDefinitions.help} {...props} />
        <DefaultLayout Page={MyLibraryV2} exact={true} path={routeDefinitions.library} {...props} />
        <DefaultLayout Page={Inbox} exact={true} path={routeDefinitions.inbox} {...props} />
        <DefaultLayout Page={InboxNew} exact={true} path={routeDefinitions.newInbox} {...props} />
        <DefaultLayout Page={WelcomePageViewer} exact={true} path="/welcomePageViewer" {...props} />
        <HeadlessLayout Page={CheckoutPage} exact={true} path={routeDefinitions.checkout} {...props} />
        <DefaultLayout Page={DashboardPage} exact={true} path={routeDefinitions.studio} {...props} />
        <DefaultLayout Page={BroadcastingInfo} exact={true} path={routeDefinitions.broadcastingInfo} {...props} />
        <DefaultLayout Page={TokenInfoContainer} path={routeDefinitions.tokenInfo.root} {...props} />
        <DefaultLayout Page={LegalContainer} path={routeDefinitions.legal} {...props} />
        <HeadlessLayout
          Page={StudioLogin}
          exact={true}
          headless={true}
          path={routeDefinitions.studioLogin}
          {...props}
        />
        <DefaultLayout Page={UserLogin} exact={true} path={routeDefinitions.userLogin} {...props} />
        <HeadlessLayout
          Page={StudioRegistration}
          exact={true}
          path={routeDefinitions.studioRegistration}
          {...props}
        />
        <HeadlessLayout
          Page={NNLogin}
          exact={true}
          path={routeDefinitions.oauth2Login}
          {...props}
        />
        <DefaultLayout Page={JSB} exact={true} headless={true} path={routeDefinitions.jsb} {...props} />
        <DefaultLayout
          Page={OnBoardingNewUser}
          exact={true}
          headless={true}
          path={routeDefinitions.onboardingNewUser}
          {...props}
        />
        <HeadlessLayout
          Page={ErrorHandler}
          exact={true}
          isButtonVisible={true}
          path={routeDefinitions.errorPage}
          {...props}
        />
        <HeadlessLayout Page={TwitterConnectPage} exact={true} path={routeDefinitions.twitterConnect} {...props} />
        <HeadlessLayout
          Page={TwitterCallbackPage}
          exact={true}
          path={routeDefinitions.twitterCallback}
          {...props}
        />

        <DefaultLayout
          Page={FanClubs}
          exact={true}
          path={routeDefinitions.fanclubs}
          {...props}
        />

        {this.privateDirectory && (
          <DefaultLayout
            Page={PrivateDirectory}
            exact={true}
            hasPartialProps={true}
            path={`${routeDefinitions.privateDirectory.root}/:filter?/:kink?`}
            {...props}
          />
        )}

        {this.awardsV2 && (
          <DefaultLayout
            Page={Contest}
            hasPartialProps={true}
            path={routeDefinitions.contest.root}
            {...props}
          />
        )}

        <DefaultLayout
          Page={WeakPasswordPage}
          exact={true}
          hasPartialProps={true}
          path={routeDefinitions.weakPasswordReset}
          {...props}
        />

        <HeadlessLayout
          Page={Broadcaster}
          exact={true}
          path={routeDefinitions.broadcaster}
          {...props}
        />
        {/*  STUDIO ONBOARDING PAGES  */}
        <DefaultLayout
          Page={StudioOwnerVerification}
          exact={true}
          path={routeDefinitions.studioWelcome}
          {...props}
        />
        <DefaultLayout
          Page={OwnerVerification}
          exact={true}
          path={routeDefinitions.studioVerification}
          {...props}
        />
        <DefaultLayout
          Page={StudioOnboardingRejectMessage}
          exact={true}
          path={routeDefinitions.studioRejected}
          {...props}
        />
        <DefaultLayout
          Page={StudioPerformerIdentities}
          exact={true}
          path={routeDefinitions.studioPerformerIdentities}
          {...props}
        />
        {/*  PERFORMER ONBOARDING PAGES  */}
        <DefaultLayout
          Page={StudioOnboardPerformerIdentity}
          exact={true}
          path={routeDefinitions.studioOnboardPerformerIdentity}
          {...props}
        />
        <DefaultLayout
          Page={OnBoardPrivacy}
          exact={true}
          path={routeDefinitions.studioOnboardPrivacy}
          {...props}
        />
        <DefaultLayout
          Page={StudioPerformerOnBoardingConfirmation}
          exact={true}
          path={routeDefinitions.studioOnboardConfirmation}
          {...props}
        />
        <DefaultLayout
          Page={StudioVerificationEmail}
          exact={true}
          path={routeDefinitions.studioEmail}
          {...props}
        />
        <DefaultLayout Page={StudioSettings} exact={true} path={routeDefinitions.studioSettings} {...props} />
        <DefaultLayout
          Page={VerifyAlternateEmail}
          exact={true}
          path={routeDefinitions.verifyAlternateEmail}
          {...props}
        />
        <HeadlessLayout
          Page={StudioManagement}
          exact={true}
          path={routeDefinitions.admin.studioManagement}
          {...props}
        />
        <HeadlessLayout
          Page={ExperimentalTools}
          exact={true}
          path={routeDefinitions.admin.experimentalTools}
          {...props}
        />
        <HeadlessLayout
          Page={AdminSeo}
          exact={true}
          path={routeDefinitions.admin.adminSEO}
          {...props}
        />
        <HeadlessLayout Page={Onboarding} exact={true} path={routeDefinitions.admin.onboarding} {...props} />
        <HeadlessLayout
          Page={DirectoryBannerAdmin}
          exact={true}
          path={routeDefinitions.admin.directoryBanner}
          {...props}
        />
        <HeadlessLayout Page={AdminBulkBan} exact={true} path={routeDefinitions.admin.bulkBan} {...props} />
        <HeadlessLayout Page={SpamDetector} exact={true} path={routeDefinitions.admin.spamDetector} {...props} />
        <HeadlessLayout Page={CreateUser} exact={true} path={routeDefinitions.admin.createUser} {...props} />
        <HeadlessLayout Page={Onboarding} exact={true} path={routeDefinitions.admin.generic} {...props} />
        <HeadlessLayout Page={Spam} exact={true} path={routeDefinitions.admin.spamLiveChat} {...props} />
        <HeadlessLayout
          Page={AdminBoostReports}
          exact={true}
          path={routeDefinitions.admin.boostReports}
          {...props}
        />
        <HeadlessLayout
          Page={AdminPrivateShowRatings}
          exact={true}
          path={routeDefinitions.admin.privateShowRatings}
          {...props}
        />
        <HeadlessLayout
          Page={AdminLanguageOverridePage}
          exact={true}
          path={routeDefinitions.admin.languageAlgorithm}
          {...props}
        />
        <HeadlessLayout Page={AdminAuthPage} exact={true} path={routeDefinitions.admin.auth} {...props} />
        <HeadlessLayout
          Page={AdminCsvMessagesPage}
          exact={true}
          path={routeDefinitions.admin.csvMessages}
          {...props}
        />

        {this.giftsRevamp && (
          <HeadlessLayout Page={AdminGifts} exact={true} path={routeDefinitions.admin.adminGifts} {...props} />
        )}

        <HeadlessLayout Page={MassGifts} exact={true} path={routeDefinitions.admin.massGifts} {...props} />
        <HeadlessLayout Page={ImageApprovalTool} exact={true} path={routeDefinitions.admin.imageApproval} />
        <HeadlessLayout Page={Awards} exact={true} path={routeDefinitions.admin.awards} {...props} />
        <HeadlessLayout
          Page={AdminSponsoredTags}
          path={routeDefinitions.admin.sponsoredTags}
          {...props}
        />

        {this.adminBannedUsernamesV2 && (
          <HeadlessLayout
            Page={AdminBannedUsers}
            exact={true}
            path={routeDefinitions.admin.bannedUsers}
            {...props}
          />
        )}

        {this.adminLogV2 && (
          <HeadlessLayout
            Page={AdminLogsPage}
            exact={true}
            path={routeDefinitions.admin.adminLogs}
            {...props}
          />
        )}

        <HeadlessLayout
          Page={AdminDashboard}
          exact={true}
          path={routeDefinitions.admin.accountDetails}
          {...props}
        />
        <DefaultLayout
          Page={DetailedRevenuePage}
          exact={true}
          path={routeDefinitions.studioEarningsReport}
          {...props}
        />
        <DefaultLayout
          Page={DailyRevenuePage}
          exact={true}
          path={routeDefinitions.studioEarningsReportDaily}
          {...props}
        />
        <DefaultLayout
          Page={TopEarningPerformers}
          exact={true}
          path={routeDefinitions.studioEarningsTopPerformers}
          {...props}
        />
        <DefaultLayout
          Page={ProfileConfirmation}
          exact={true}
          path={routeDefinitions.studioProfileConfirmation}
          {...props}
        />
        <DefaultLayout Page={DashboardPage} exact={true} path={routeDefinitions.studioDashboard} {...props} />
        <DefaultLayout
          Page={AddExistingPerformer}
          exact={true}
          path={routeDefinitions.studioAddExistingPerformer}
          {...props}
        />
        <DefaultLayout Page={StudioInvitation} exact={true} path={routeDefinitions.studioInvitation} {...props} />
        <DefaultLayout Page={RosterManagement} exact={true} path={routeDefinitions.studioRoster} {...props} />
        <DefaultLayout Page={UpgradeToGoldPage} exact={true} path={routeDefinitions.gold} {...props} />
        <DefaultLayout Page={Countries} exact={true} path={routeDefinitions.countries} {...props} />
        <DefaultLayout Page={Languages} exact={true} path={routeDefinitions.languages} {...props} />
        <DefaultLayout Page={AppBroadcasting} exact={true} path={routeDefinitions.appBroadcasting} {...props} />
        <DefaultLayout Page={Contact} exact={true} path={routeDefinitions.contact} {...props} />
        <DefaultLayout
          Page={PerformerProfile}
          exact={true}
          path={routeDefinitions.studioPerformerProfile}
          {...props}
        />
        <DefaultLayout
          Page={PerformerEditProfile}
          exact={true}
          path={routeDefinitions.studioEditPerformerProfile}
          {...props}
        />

        <DefaultLayout
          Page={Achievements}
          exact={true}
          path={routeDefinitions.achievements}
          {...props}
        />
        <DefaultLayout Page={Directory} path={routeDefinitions.noProfile} {...props} />
        <DefaultLayout Page={Directory} exact={true} path={routeDefinitions.pageNotFound} {...props} />
        <DefaultLayout
          Page={Directory}
          exact={true}
          path={`/:genderPreference${GENDER_PREFERENCE_REGEX}/:filter*`}
          {...props}
        />
        <DefaultLayout
          Page={Directory}
          exact={true}
          path={POPULAR_CAMS_REGEX}
          {...props}
        />
        <DefaultLayout
          Page={Directory}
          exact={true}
          path={DIRECTORY_CAMS_ALIASES}
          {...props}
        />
        <DefaultLayout
          Page={Directory}
          exact={true}
          path={routeDefinitions.checkoutTokens}
          {...props}
        />
        <DefaultLayout
          Page={Directory}
          exact={true}
          path={routeDefinitions.tags}
          {...props}
        />
        <DefaultLayout
          Page={EmailVerification}
          exact={true}
          path={routeDefinitions.emailVerification}
          {...props}
        />
        <DefaultLayout Page={Favorites} exact={true} path={routeDefinitions.favorites} {...props} />
        <Route
          component={VerifyMyAge}
          path={[
            routeDefinitions.verifyMyAge,
            routeDefinitions.verifyMyAge.toLowerCase(),
          ]}
        />

        {/* 404 Page */}
        <DefaultLayout Page={LastCashOut} exact={true} path={routeDefinitions.lastCashOut} {...props} />
        <DefaultLayout
          Page={BitsafeClosePage}
          exact={true}
          hasPartialProps={true}
          path={routeDefinitions.bitsafeClosePage}
          {...props}
        />

        <DefaultLayout Page={Settings} path={routeDefinitions.profileSettings} {...props} />
        <DefaultLayout Page={Calendar} exact={true} path={routeDefinitions.calendar} {...props} />

        <Route path={routeDefinitions.profileV2.root}>
          <UserProfile {...props} />
        </Route>

        <DefaultLayout Page={RedirectingNotFound} path="*" {...props} />
      </Switch>
    );
  }
}
